import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InSessionView } from '@eruvaka/web-pondlogs-main';
import { useAuth } from '../AuthContext';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { LandingPage } from './LandingPage';
const PW_EXPIRY_DISMISSED_KEY = 'pondlogs_pw_expiry_dismissed';
const formatRoleLabel = (value) => {
    if (!value)
        return 'Farm Manager';
    return value
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
};
const useLocationCount = () => {
    const [locationCount, setLocationCount] = useState(null);
    const load = useCallback(async () => {
        try {
            const { fetchLocations } = await import('@eruvaka/web-pondlogs-ponds');
            const locations = await fetchLocations();
            setLocationCount(locations.length);
        }
        catch {
            setLocationCount(null);
        }
    }, []);
    useEffect(() => {
        void load();
    }, [load]);
    return { locationCount };
};
export const MainView = () => {
    const { user, signOut, passwordExpiring, daysUntilExpiry } = useAuth();
    const { t } = useTranslation('main');
    const navigate = useNavigate();
    const { locationCount } = useLocationCount();
    const dismissedKey = user ? `${PW_EXPIRY_DISMISSED_KEY}_${user._id}` : null;
    const [isDismissed, setIsDismissed] = useState(() => {
        if (!dismissedKey)
            return false;
        return localStorage.getItem(dismissedKey) === '1';
    });
    const notifications = useMemo(() => {
        if (!passwordExpiring || isDismissed || daysUntilExpiry === undefined)
            return [];
        const msg = daysUntilExpiry === 1
            ? t('notifications.passwordExpiry.messageOne', { count: daysUntilExpiry })
            : t('notifications.passwordExpiry.messageOther', { count: daysUntilExpiry });
        return [
            {
                id: 'pw-expiry',
                title: t('notifications.passwordExpiry.title'),
                message: msg,
                timestamp: new Date().toISOString(),
                isRead: false,
            },
        ];
    }, [passwordExpiring, daysUntilExpiry, isDismissed, t]);
    const handleMarkAllAsRead = () => {
        if (dismissedKey) {
            localStorage.setItem(dismissedKey, '1');
        }
        setIsDismissed(true);
    };
    const displayName = user?.full_name ?? user?.email ?? 'Unknown User';
    const roleLabel = formatRoleLabel(user?.user_type ?? user?.allowed_roles?.[0]);
    const userProfile = user
        ? {
            ...user,
            firstName: user.first_name,
            lastName: user.last_name,
            status: user.status,
        }
        : undefined;
    const showSetupLanding = locationCount === 0;
    const handleStartSetup = () => {
        navigate('/ponds');
    };
    return (_jsx(InSessionView, { displayName: displayName, roleLabel: roleLabel, onSignOut: signOut, user: userProfile, notifications: notifications, onMarkAllAsRead: handleMarkAllAsRead, pondsEmptyMapContent: _jsx(LandingPage, { onStartSetup: handleStartSetup }), showSetupLanding: showSetupLanding, onStartSetup: handleStartSetup }));
};
